<template>
    <page-controls>
        <template v-slot:left>
            <div class="grow @3xl/controls:max-w-md">
                <vx-filterable :active="active" @toggle="toggleFilterable" data-test="flows-filters">
                    <template v-slot:placeholder>
                        <span class="mr-2">Find</span>
                        <vx-badge
                            v-if="filtersCount > 0"
                            data-test="flows-reset"
                            :borders="false"
                            size="xs"
                            @click.stop="reset"
                        >
                            {{ pluralized('filter', filtersCount, true) }}
                            <font-awesome-icon widthAuto :icon="faCircleXmark" />
                        </vx-badge>
                    </template>
                    <template v-slot:form>
                        <form @submit.prevent="apply" class="flex flex-col gap-4">
                            <div>
                                <filter-label> Type </filter-label>
                                <div>
                                    <vx-checkbox-selection v-model="filters.type" class="flex-row flex gap-1">
                                        <vx-checkbox-selection-item value="playbook" class="grow">
                                            Playbook
                                        </vx-checkbox-selection-item>
                                        <vx-checkbox-selection-item value="custom_flow" class="grow">
                                            Custom Flow
                                        </vx-checkbox-selection-item>
                                    </vx-checkbox-selection>
                                </div>
                            </div>

                            <div>
                                <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                                    Keyword Search
                                </filter-label>
                                <vx-input
                                    type="text"
                                    data-test="flows-query"
                                    placeholder="Search"
                                    maxlength="80"
                                    size="lg"
                                    v-model="filters.query"
                                >
                                    <template v-slot:right>
                                        <font-awesome-icon widthAuto class="text-xs" :icon="faMagnifyingGlass" />
                                    </template>
                                </vx-input>
                            </div>
                            <div>
                                <filter-label :canReset="!!filters.status" @reset="filters.status = undefined">
                                    Status
                                </filter-label>
                                <vx-selectable
                                    :options="[
                                        {
                                            label: 'Live',
                                            value: 'active',
                                        },
                                        {
                                            label: 'Draft',
                                            value: 'draft',
                                        },
                                        {
                                            label: 'Paused',
                                            value: 'paused',
                                        },
                                    ]"
                                    :reduce="(option) => option.value"
                                    label="label"
                                    :clearable="false"
                                    placeholder="Status"
                                    size="lg"
                                    v-model="filters.status"
                                >
                                </vx-selectable>
                            </div>
                            <div>
                                <filter-label :canReset="!!filters.playbook" @reset="filters.playbook = undefined">
                                    Base Playbook
                                </filter-label>
                                <vx-selectable
                                    :options="playbookGallery"
                                    :reduce="(option) => option.type"
                                    label="name"
                                    :clearable="false"
                                    placeholder="Base Playbook"
                                    size="lg"
                                    v-model="filters.playbook"
                                >
                                </vx-selectable>
                            </div>

                            <div>
                                <filter-label
                                    :canReset="filters.category != undefined"
                                    @reset="filters.category = undefined"
                                >
                                    Category
                                </filter-label>
                                <category-filter-select v-model="filters.category" size="lg"></category-filter-select>
                            </div>

                            <div class="flex gap-2 pt-4">
                                <vx-button
                                    data-test="flows-cancel"
                                    type="button"
                                    size="lg"
                                    color="muted-light"
                                    @click="cancel"
                                >
                                    Cancel
                                </vx-button>
                                <vx-button data-test="flows-apply" size="lg" type="submit" block>
                                    Apply
                                    <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                                </vx-button>
                            </div>
                        </form>
                    </template>
                </vx-filterable>
            </div>
            <div class="flex w-full gap-2 @xl/controls:w-auto">
                <vx-button-group size="lg" class="w-full">
                    <vx-select
                        v-model="filters.sort_field"
                        @update:modelValue="apply"
                        :options="[
                            {
                                value: 'name',
                                label: 'Sort by: Name',
                            },
                            {
                                value: 'created_at',
                                label: 'Sort by: Created',
                            },
                            {
                                value: 'updated_at',
                                label: 'Sort by: Updated',
                            },
                        ]"
                    ></vx-select>
                    <vx-button
                        @click.prevent="
                            filters.sort_dir = filters.sort_dir === 'asc' ? 'desc' : 'asc';
                            apply();
                        "
                        type="button"
                        color="white"
                        shadow
                    >
                        <font-awesome-icon
                            widthAuto
                            :icon="props.modelValue.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                        ></font-awesome-icon>
                    </vx-button>
                </vx-button-group>
            </div>
        </template>
        <template v-slot:right>
            <new-flow-button
                data-test="flows-new"
                block
                @click.prevent="emit('new')"
                size="lg"
                :disabled="!!(loading || list.length >= FLOWS_LIMIT)"
            >
            </new-flow-button>
        </template>
    </page-controls>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import {
    VxBadge,
    VxButton,
    VxButtonGroup,
    VxFilterable,
    VxInput,
    VxSelect,
    VxCheckboxSelection,
    VxCheckboxSelectionItem,
    VxSelectable,
} from '@voxie/frontend-components';
import {
    faCircleCheck,
    faMagnifyingGlass,
    faCircleXmark,
    faArrowDownWideShort,
    faArrowUpWideShort,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import PageControls from '~/components/general/PageControls.vue';
import NewFlowButton from './components/NewFlowButton.vue';

import { FLOWS_LIMIT, playbookGallery } from './playbook/playbook';
import CategoryFilterSelect from '~/components/general/categories/CategoryFilterSelect.vue';
import FilterLabel from '~/components/general/FilterLabel.vue';
import { pluralized } from '~/components/filters';

const filters = ref({});
const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
    list: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: false,
    },
});
watch(
    () => props.modelValue,
    (modelValue) => {
        filters.value = { ...modelValue };
    },
    {
        deep: true,
        immediate: true,
    }
);

const emit = defineEmits(['update:modelValue', 'apply', 'reset', 'new']);

const active = ref(false);

const toggleFilterable = (event) => {
    active.value = event;
};

const filtersCount = computed(
    () =>
        (props.modelValue.query ? 1 : 0) +
        (props.modelValue.status ? 1 : 0) +
        (props.modelValue.playbook ? 1 : 0) +
        (props.modelValue.type?.length ? 1 : 0) +
        (props.modelValue.category?.length ? 1 : 0)
);

const reset = () => {
    active.value = false;
    emit('reset');
};

const cancel = async () => {
    filters.value = { ...props.modelValue };
    active.value = false;
};

const apply = async () => {
    active.value = false;
    emit('update:modelValue', { ...filters.value });
    await nextTick();
    emit('apply');
};
</script>
